import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { useAuth } from '../context/AuthContext';
import PhoneInput from '../components/PhoneInput';
import { ArrowLeft, Eye, EyeOff, MessageSquare, CreditCard } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE_URL = `${BACKEND_URL}/api`;

const EditUser = () => {
  const { token, getAuthHeaders, isAdmin, isSuperAdmin } = useAuth();
  const navigate = useNavigate();
  const { userId } = useParams();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [availablePlans, setAvailablePlans] = useState([]);
  const [userRole, setUserRole] = useState('');
  
  const [formData, setFormData] = useState({
    full_name: '',
    email: '',
    phone: '',
    password: '',
    accepts_sms: false,
    account_status: 'active',
    plan_type: '',
    plan_status: 'active'
  });
  
  const [calendarNames, setCalendarNames] = useState('');

  const fetchUser = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_BASE_URL}/admin/users/${userId}`, {
        headers: getAuthHeaders()
      });
      const user = response.data.data;
      setUserRole(user.role || '');
      // Capitalize plan_type to match plan names (e.g., "pro" -> "Pro")
      const planType = user.plan_type 
        ? user.plan_type.charAt(0).toUpperCase() + user.plan_type.slice(1).toLowerCase()
        : 'Free';
      setFormData({
        full_name: user.full_name,
        email: user.email,
        phone: user.phone || '',
        password: '',
        accepts_sms: user.accepts_sms || false,
        account_status: user.is_active ? 'active' : 'hold',
        plan_type: planType,
        plan_status: user.plan_status || 'active'
      });
      setCalendarNames(user.calendar_names || 'None');
    } catch (err) {
      console.error('Error fetching user:', err);
      setError('Failed to load user');
    } finally {
      setLoading(false);
    }
  }, [userId, getAuthHeaders]);

  const fetchPlans = useCallback(async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/plans/`);
      setAvailablePlans(response.data || []);
    } catch (err) {
      console.error('Error fetching plans:', err);
    }
  }, []);

  useEffect(() => {
    if (!isAdmin()) {
      navigate('/dashboard');
      return;
    }
    fetchUser();
    // Fetch plans if super admin
    if (isSuperAdmin()) {
      fetchPlans();
    }
  }, [isAdmin, isSuperAdmin, navigate, fetchUser, fetchPlans]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
    setError('');
    setSuccess('');
  };

  const handlePhoneChange = (value) => {
    setFormData({
      ...formData,
      phone: value
    });
    setError('');
    setSuccess('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setSaving(true);

    try {
      const userData = {
        full_name: formData.full_name,
        email: formData.email,
        phone: formData.phone || null,
        accepts_sms: formData.accepts_sms,
        is_active: formData.account_status === 'active'
      };

      // Only include password if it's provided
      if (formData.password) {
        userData.password = formData.password;
      }

      // Super Admin can update plan for Calendar Admins
      if (isSuperAdmin() && userRole === 'calendar_admin') {
        userData.plan_type = formData.plan_type;
        userData.plan_status = formData.plan_status;
      }

      await axios.put(
        `${API_BASE_URL}/admin/users/${userId}`,
        userData,
        { headers: getAuthHeaders() }
      );

      setSuccess('User updated successfully!');
      setTimeout(() => {
        navigate('/admin/users');
      }, 1500);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to update user');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen">
        <Header />
        <div className="flex items-center justify-center py-20">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-yellow-500"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <section className="py-12">
        <div className="max-w-3xl mx-auto px-6">
          <div className="mb-8">
            <button
              onClick={() => navigate('/admin/users')}
              className="flex items-center text-gray-600 hover:text-gray-900 mb-4"
            >
              <ArrowLeft className="w-5 h-5 mr-2" />
              Back to Users
            </button>
            <h1 className="text-3xl font-bold text-gray-800">Edit User</h1>
            <p className="text-gray-600 mt-2">Update user information</p>
          </div>

          {error && (
            <div className="mb-6 p-4 bg-red-100 border border-red-400 text-red-700 rounded">
              {error}
            </div>
          )}

          {success && (
            <div className="mb-6 p-4 bg-green-100 border border-green-400 text-green-700 rounded">
              {success}
            </div>
          )}

          <div className="bg-white rounded-lg shadow p-8">
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Full Name *
                </label>
                <input
                  type="text"
                  name="full_name"
                  value={formData.full_name}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Email *
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  className="w-full px-4 py-3 border border-gray-300 rounded-md bg-gray-100 cursor-not-allowed"
                  disabled
                />
                <p className="text-sm text-gray-500 mt-1">Email cannot be changed</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Calendar Name *
                </label>
                <input
                  type="text"
                  name="calendar_names"
                  value={calendarNames}
                  className="w-full px-4 py-3 border border-gray-300 rounded-md bg-gray-100 cursor-not-allowed"
                  disabled
                  readOnly
                />
                <p className="text-sm text-gray-500 mt-1">Calendar associations cannot be changed here</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Phone
                </label>
                <PhoneInput
                  value={formData.phone}
                  onChange={handlePhoneChange}
                  className="w-full"
                  data-testid="edit-user-phone"
                />
                {/* Accept SMS Checkbox */}
                <label className="flex items-start mt-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.accepts_sms}
                    onChange={(e) => setFormData({...formData, accepts_sms: e.target.checked})}
                    disabled={!formData.phone}
                    className="mt-0.5 h-4 w-4 text-yellow-500 border-gray-300 rounded focus:ring-yellow-500 disabled:opacity-50"
                  />
                  <span className={`ml-2 text-sm ${formData.phone ? 'text-gray-700' : 'text-gray-400'}`}>
                    <MessageSquare className="w-4 h-4 inline mr-1" />
                    User accepts SMS notifications
                  </span>
                </label>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  New Password
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? "text" : "password"}
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-yellow-500 focus:border-transparent pr-12"
                    placeholder="Leave blank to keep current password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
                <p className="text-sm text-gray-500 mt-1">Only fill if you want to change the password</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Account Status *
                </label>
                <select
                  name="account_status"
                  value={formData.account_status}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                  required
                >
                  <option value="active">Active</option>
                  <option value="hold">Hold</option>
                </select>
                <p className="text-sm text-gray-500 mt-1">
                  {formData.account_status === 'active' 
                    ? 'User can access their account and perform actions' 
                    : 'User account is on hold and access is restricted'}
                </p>
              </div>

              {/* Plan Subscription - Only visible for Super Admin editing Calendar Admins */}
              {isSuperAdmin() && userRole === 'calendar_admin' && (
                <div className="border-t pt-6 mt-6">
                  <div className="flex items-center mb-4">
                    <CreditCard className="w-5 h-5 text-yellow-500 mr-2" />
                    <h3 className="text-lg font-semibold text-gray-800">Plan Subscription</h3>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Plan Type
                      </label>
                      <select
                        name="plan_type"
                        value={formData.plan_type}
                        onChange={handleChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                      >
                        {availablePlans.map((plan) => (
                          <option key={plan.id || plan.name} value={plan.name}>
                            {plan.name} - ${plan.price}/{plan.billing_cycle || 'month'}
                          </option>
                        ))}
                      </select>
                      <p className="text-sm text-gray-500 mt-1">
                        Select the subscription plan for this Calendar Admin
                      </p>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Plan Status
                      </label>
                      <select
                        name="plan_status"
                        value={formData.plan_status}
                        onChange={handleChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                      >
                        <option value="active">Active</option>
                        <option value="on_hold">On Hold</option>
                        <option value="cancelled">Cancelled</option>
                      </select>
                      <p className="text-sm text-gray-500 mt-1">
                        {formData.plan_status === 'active' 
                          ? 'Subscription is active and all features are enabled'
                          : formData.plan_status === 'on_hold'
                          ? 'Subscription is paused - limited functionality'
                          : 'Subscription is cancelled'}
                      </p>
                    </div>
                  </div>
                  
                  <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
                    <p className="text-sm text-yellow-800">
                      <strong>Note:</strong> Manually changing the plan will not affect Stripe billing. 
                      Use this for promotional upgrades, trials, or troubleshooting purposes.
                    </p>
                  </div>
                </div>
              )}

              <div className="flex justify-end space-x-3 pt-6 border-t">
                <button
                  type="button"
                  onClick={() => navigate('/admin/users')}
                  className="px-6 py-3 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="px-6 py-3 bg-yellow-500 hover:bg-yellow-600 disabled:bg-yellow-300 text-white rounded-md transition-colors"
                >
                  {saving ? 'Updating...' : 'Update User'}
                </button>
              </div>
            </form>
          </div>
        </div>
      </section>
      <Footer />
    </div>
  );
};

export default EditUser;
